/**
 * Shape extracted draft products to match StepOne / QuoteDetails field trees.
 */

const BOX_FIELDS = [
  'boxType',
  'height',
  'width',
  'depth',
  'material',
  'productType',
  'productWeight',
  'size',
  'bottomClosure',
  'thickness',
  'specialFeatures',
  'printingFinishes',
  'substrateType',
  'childResistant',
  'requestThicknessRecommendation',
  'printingFeatures',
];

const BAG_FIELDS = [
  'bagType', 'material', 'width', 'height', 'gusset', 'gussetType', 'size',
  'printingFinishes', 'printingFeatures', 'bagSpecifications', 'closureType',
  'zipperType', 'substrateType', 'thickness',
];
const BLISTER_FIELDS = ['blisterType', 'material', 'size', 'hangingHole', 'printingFeatures', 'plasticShell', 'notes'];
const CAP_FIELDS = ['capType', 'additionalMaterialsForCaps', 'childProofForCaps', 'colorForCaps', 'size', 'artwork'];
const LABEL_FIELDS = [
  'core', 'shape', 'material', 'finish', 'applyMethod', 'specialEffects',
  'rollingDirection', 'size', 'productDescription',
];
const SHRINK_FIELDS = ['material', 'finishes', 'application', 'rollingDirection', 'specialEffects', 'size'];
const SACHET_FIELDS = ['material', 'size', 'specialFinishes', 'thickness'];
const ENVELOPE_FIELDS = ['material', 'size', 'secondaryMaterial', 'thickness', 'printingFinishes', 'specialFeatures'];
const MAILER_FIELDS = ['material', 'size', 'secondaryMaterial', 'bubbleOption', 'thickness', 'printingFinishes', 'substrateType', 'specialFeatures'];
const BOTTLE_FIELDS = [
  'materialGroup',
  'materialSubGroup',
  'capacity',
  'bottleColor',
  'bottleCap',
  'bottleSize',
  'threading',
  'bottleImage',
  'artwork',
];
const GENERIC_FIELDS = ['material', 'size', 'referenceImageUrl', 'productDescription'];

function hasValue(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  if (typeof value === 'number') return !Number.isNaN(value);
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(value);
}

function pickFields(source = {}, allowedKeys = []) {
  const picked = {};
  allowedKeys.forEach((key) => {
    const value = source[key];
    if (hasValue(value)) {
      picked[key] = value;
    }
  });
  return picked;
}

function syncBoxSize(product) {
  const fields = { ...(product.fields || {}) };
  const height = fields.height ? String(fields.height).trim() : '';
  const width = fields.width ? String(fields.width).trim() : '';
  const depth = fields.depth ? String(fields.depth).trim() : '';
  const isCylinder = String(fields.boxType) === '5';

  if (width && height) {
    product.sizeNote = isCylinder || !depth
      ? `${width} x ${height} mm`
      : `${width} x ${height} x ${depth} mm`;
    fields.size = {
      widthMM: width,
      heightMM: height,
      ...(depth && !isCylinder ? { depthMM: depth, depth } : {}),
    };
  } else if (product.sizeNote && !fields.size) {
    const nums = String(product.sizeNote).match(/[\d.]+/g);
    if (nums && nums.length >= 2) {
      fields.size = {
        widthMM: nums[0],
        heightMM: nums[1],
        ...(nums[2] ? { depthMM: nums[2], depth: nums[2] } : {}),
      };
    }
  }

  product.fields = fields;
  return product;
}

function syncBagSize(product) {
  const fields = { ...(product.fields || {}) };
  if (fields.width && fields.height) {
    product.sizeNote = fields.gusset
      ? `${fields.width} x ${fields.height} x ${fields.gusset} mm`
      : `${fields.width} x ${fields.height} mm`;
    fields.size = {
      widthMM: String(fields.width),
      heightMM: String(fields.height),
      ...(fields.gusset ? { gussetMM: String(fields.gusset) } : {}),
    };
  } else if (product.sizeNote && !fields.size) {
    const nums = String(product.sizeNote).match(/[\d.]+/g);
    if (nums && nums.length >= 2) {
      fields.size = {
        widthMM: nums[0],
        heightMM: nums[1],
        ...(nums[2] ? { depthMM: nums[2], depth: nums[2] } : {}),
      };
    }
  }
  product.fields = fields;
  return product;
}

function normalizeSingleProduct(rawProduct = {}) {
  const productType = String(rawProduct.productType || '').trim();
  const base = {
    productType,
    SKU: rawProduct.SKU ?? 1,
    quantitiesCount: rawProduct.quantitiesCount ?? 1,
    skuDetails: Array.isArray(rawProduct.skuDetails) ? rawProduct.skuDetails : [],
    sizeNote: rawProduct.sizeNote || '',
    printingStyle: rawProduct.printingStyle || '',
    productionType: rawProduct.productionType || '',
    colorMatch: rawProduct.colorMatch || 'No',
    isReprint: rawProduct.isReprint || 'No',
    colorMatchOrderNumber: rawProduct.colorMatchOrderNumber || '',
    previousInvoiceNumber: rawProduct.previousInvoiceNumber || '',
    specialNotesToProduction: rawProduct.specialNotesToProduction || '',
    address: rawProduct.address || '',
    packagingInstructions: rawProduct.packagingInstructions || '',
    shippingInstructions: rawProduct.shippingInstructions || '',
    imageUrls: rawProduct.imageUrls || [],
    colorMatchArtworkUrls: rawProduct.colorMatchArtworkUrls || [],
    fields: {},
  };

  const sourceFields = rawProduct.fields || {};

  switch (productType) {
    case 'Boxes': {
      base.fields = pickFields(sourceFields, BOX_FIELDS);
      if (hasValue(base.fields.boxType)) {
        base.fields.boxType = String(base.fields.boxType).match(/^(\d+)/)?.[1] || String(base.fields.boxType);
      }
      syncBoxSize(base);
      break;
    }
    case 'ShrinkSleeves':
      base.fields = pickFields(sourceFields, SHRINK_FIELDS);
      syncBagSize(base);
      break;
    case 'Sachets':
      base.fields = pickFields(sourceFields, SACHET_FIELDS);
      syncBagSize(base);
      break;
    case 'Envelopes':
      base.fields = pickFields(sourceFields, ENVELOPE_FIELDS);
      syncBagSize(base);
      break;
    case 'MailerBags':
      base.fields = pickFields(sourceFields, MAILER_FIELDS);
      syncBagSize(base);
      break;
    case 'Labels':
      base.fields = pickFields(sourceFields, LABEL_FIELDS);
      syncBagSize(base);
      break;
    case 'Bags':
      base.fields = pickFields(sourceFields, BAG_FIELDS);
      syncBagSize(base);
      break;
    case 'Blisters':
      base.fields = pickFields(sourceFields, BLISTER_FIELDS);
      syncBagSize(base);
      break;
    case 'Bottles':
      base.fields = pickFields(sourceFields, BOTTLE_FIELDS);
      break;
    case 'Caps':
      base.fields = pickFields(sourceFields, CAP_FIELDS);
      syncBagSize(base);
      break;
    default:
      base.fields = pickFields(sourceFields, GENERIC_FIELDS);
      if (productType && !base.fields.material && hasValue(sourceFields.material)) {
        base.fields.material = sourceFields.material;
      }
      if (base.sizeNote) {
        const nums = String(base.sizeNote).match(/[\d.]+/g);
        if (nums && nums.length >= 2) {
          base.fields.size = {
            widthMM: nums[0],
            heightMM: nums[1],
            ...(nums[2] ? { depthMM: nums[2], depth: nums[2] } : {}),
          };
        }
      }
      break;
  }

  return base;
}

export function normalizeQuoteProducts(products = []) {
  if (!products.length) {
    return [normalizeSingleProduct({ productType: '', fields: {} })];
  }
  return products.map((product) => normalizeSingleProduct(product));
}

export function validateQuoteProduct(product) {
  const type = product?.productType?.trim();
  if (!type) {
    return 'Product type is required.';
  }

  const fields = product?.fields || {};

  if (type === 'Boxes') {
    if (!fields.boxType) return 'Box type is required.';
    if (!fields.height || !fields.width) return 'Box height and width are required.';
    if (String(fields.boxType) !== '5' && !fields.depth) return 'Box depth is required for this box type.';
    if (!fields.material) return 'Box material is required.';
  }

  if (type === 'Labels') {
    if (!fields.shape) return 'Label shape is required.';
    if (!fields.material) return 'Label material is required.';
    if (!fields.finish) return 'Label finish is required.';
    if (!fields.size?.widthMM || !fields.size?.heightMM) return 'Label width and height are required.';
  }

  if (type === 'Bags' && !fields.bagType) return 'Bag type is required.';
  if (type === 'Blisters' && !fields.blisterType) return 'Blister type is required.';
  if (type === 'Caps' && !fields.capType) return 'Cap type is required.';

  if (!product.printingStyle) return 'Printing style is required.';
  if (!product.productionType) return 'Production type is required.';
  if (!product.SKU) return 'SKU count is required.';

  return null;
}
